/*
 * C2C Connections Panel Template for RSC routes
 */
import type {
  C2CConnectionsRSCPanelArea,
  PanelArea,
  PanelsBreakpointTemplate,
  PanelsTemplate,
} from '../types';
import {
  DETAILS_DEFAULT_WIDTH,
  DETAILS_MIN_WIDTH,
  LARGE_NAV_DEFAULT_WIDTH,
  MEDIUM_NAV_MAX_WIDTH,
  MEDIUM_NAV_MIN_WIDTH,
  PROJECT_NAV_MIN_WIDTH,
} from './constants';
import TEXT from './generated/TEXT/c2cConnectionsRSC';

TEXT.DEFINE({
  directoryNav: {
    accessibility: 'Toggle the Project Navigation',
    hide: 'Hide Project Navigation',
    show: 'Show Project Navigation',
  },
  details: {
    accessibility: 'Toggle the Asset Info',
    hide: 'Hide Asset Info',
    show: 'Show Asset Info',
  },
});

/**
 * Base template (Mobile +)
 */
export function baseTemplate(
  __visible: Array<PanelArea>
): PanelsBreakpointTemplate {
  return {
    areas: [['directoryNav', 'directoryContent', 'viewer', 'details', 'ai']],
    resizers: {
      directoryNav: 'right',
      viewer: 'right',
      directoryContent: 'right',
    },
    columns: [
      /* Project Nav uses the PrimarySheetNav tunnel on smaller screens so it's always hidden. */
      {
        key: 'directoryNav',
        defaultSize: MEDIUM_NAV_MIN_WIDTH,
        isToggleable: false,
        showing: false,
      },

      /* Devices Grid */
      {
        key: 'directoryContent',
        defaultMin: 300,
        defaultSize: '1fr',
        isToggleable: false,
        showing: true,
      },

      /* Viewer Panel */
      {
        key: 'viewer',
        defaultMin: 376,
        defaultSize: '1fr',
        isToggleable: false,
        showing: false,
      },

      /* Device Details Panel */
      {
        key: 'details',
        defaultMin: DETAILS_DEFAULT_WIDTH,
        defaultSize: DETAILS_DEFAULT_WIDTH,
        isToggleable: false,
        showing: false,
      },

      /* AI Panel (hidden by default) */
      {
        key: 'ai',
        defaultMin: 0,
        defaultSize: 0,
        isToggleable: false,
        showing: false,
      },
    ],
    rows: [
      {
        key: 'defaultRow',
        defaultMin: 200,
        defaultSize: '1fr',
        isToggleable: false,
      },
    ],
  };
}

/**
 * Medium template
 */
export const mediumTemplate = (
  visible: Array<PanelArea>
): PanelsBreakpointTemplate => {
  return {
    areas: [['directoryNav', 'directoryContent', 'viewer', 'details', 'ai']],
    resizers: {
      directoryNav: 'right',
      viewer: 'right',
      directoryContent: 'right',
    },
    columns: [
      /* Project Nav */
      {
        key: 'directoryNav',
        defaultMin: PROJECT_NAV_MIN_WIDTH,
        defaultSize: PROJECT_NAV_MIN_WIDTH,
        defaultMax: MEDIUM_NAV_MAX_WIDTH,
        isToggleable: true,
        panelToggle: {
          variant: 'left',
          label: TEXT.directoryNav,
        },
        showing: visible.includes('directoryNav'),
      },

      /* Devices Grid */
      {
        key: 'directoryContent',
        defaultMin: 300,
        defaultSize: '1fr',
        isToggleable: false,
        showing: true,
      },

      /* Viewer Panel */
      {
        key: 'viewer',
        defaultMin: 376,
        defaultSize: '1fr',
        isToggleable: false,
        showing: false,
      },

      /* Device Details Panel */
      {
        key: 'details',
        defaultMin: 300,
        defaultSize: '1fr',
        defaultMax: 376,
        isToggleable: true,
        showing: visible.includes('details'),
      },

      /* AI Panel (hidden by default) */
      {
        key: 'ai',
        defaultMin: 0,
        defaultSize: 0,
        isToggleable: false,
        showing: false,
      },
    ],
    rows: [
      {
        key: 'defaultRow',
        defaultMin: 200,
        defaultSize: '1fr',
        isToggleable: false,
      },
    ],
  };
};

/**
 * Large template
 */
export const largeTemplate = (
  visible: Array<PanelArea>
): PanelsBreakpointTemplate => {
  return {
    areas: [['directoryNav', 'directoryContent', 'viewer', 'details', 'ai']],
    resizers: {
      directoryNav: 'right',
      viewer: 'right',
      directoryContent: 'right',
    },
    columns: [
      /* Project Nav */
      {
        key: 'directoryNav',
        defaultMin: PROJECT_NAV_MIN_WIDTH,
        defaultSize: LARGE_NAV_DEFAULT_WIDTH,
        defaultMax: MEDIUM_NAV_MAX_WIDTH,
        isToggleable: true,
        panelToggle: {
          variant: 'left',
          label: TEXT.directoryNav,
        },
        showing: visible.includes('directoryNav'),
      },

      /* Devices Grid */
      {
        key: 'directoryContent',
        defaultMin: 300,
        defaultSize: '1fr',
        isToggleable: false,
        showing: true,
      },

      /* Viewer Panel */
      {
        key: 'viewer',
        defaultMin: 376,
        defaultSize: '1fr',
        isToggleable: false,
        showing: false,
      },

      /* Device Details Panel */
      {
        key: 'details',
        defaultMin: DETAILS_MIN_WIDTH,
        defaultSize: DETAILS_DEFAULT_WIDTH,
        defaultMax: 600,
        isToggleable: true,
        panelToggle: {
          variant: 'right',
          label: TEXT.details,
        },
        showing: visible.includes('details'),
      },

      /* AI Panel (hidden by default) */
      {
        key: 'ai',
        defaultMin: 0,
        defaultSize: 0,
        isToggleable: false,
        showing: false,
      },
    ],
    rows: [
      {
        key: 'defaultRow',
        defaultMin: 200,
        defaultSize: '1fr',
        isToggleable: false,
      },
    ],
  };
};

export function getPanelState(visible: Array<PanelArea>): PanelsTemplate {
  return {
    base: baseTemplate(visible),
    tablet: mediumTemplate(visible),
    desktop: largeTemplate(visible),
    visible,
  };
}

export const getDefaultVisiblePanels =
  (): Array<C2CConnectionsRSCPanelArea> => {
    return ['directoryNav', 'directoryContent', 'details'];
  };

// Because this page encapsulates three "pages", each has a different layout default
export const getDefaultPanelState = (): PanelsTemplate => {
  return getPanelState(getDefaultVisiblePanels());
};
